import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { GameWeather } from '@/services/weather/types';
import type { MlbWeatherResponse } from '@/services/weather/mlb';
import { weatherMapKey } from '@/services/weather/key';

async function fetchWeather(): Promise<MlbWeatherResponse> {
  const r = await fetch('/api/weather/mlb');
  if (!r.ok) throw new Error(`weather ${r.status}`);
  return r.json();
}

export interface WeatherLookup {
  weatherMap: Record<string, GameWeather> | undefined;
  getWeather: (homeAbbr: string | undefined, gameTimeIso: string | undefined) => GameWeather | null;
  isLoading: boolean;
}

/**
 * Shared MLB weather fetcher. Call once at the top of a view; pass the `getWeather`
 * lookup down. Disabled when `enabled=false` so NBA pages don't make the request.
 */
export function useMlbWeather(enabled: boolean = true): WeatherLookup {
  const { data, isLoading } = useQuery({
    queryKey: ['mlb-weather'] as const,
    queryFn: fetchWeather,
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

  const map = useMemo(() => (enabled ? data?.weather : undefined), [data, enabled]);

  const getWeather = useCallback<WeatherLookup['getWeather']>(
    (homeAbbr, gameTimeIso) => {
      if (!map || !homeAbbr || !gameTimeIso) return null;
      return map[weatherMapKey(homeAbbr, gameTimeIso)] ?? null;
    },
    [map],
  );

  return { weatherMap: map, getWeather, isLoading };
}
